// M6 Point Whack-a-Mole — point at moles popping out of a 3x3 grid.
// Spec: /microgames/06-point-mole.md
// Input channel: hands.right.pointTarget ?? hands.left.pointTarget.

import type {
  GameContext,
  InputFrame,
  Microgame,
  MicrogameResult,
  Point,
} from '@pose-game/core';

const DURATION_SEC = 22;
const GRID = 3;
const NUM_HOLES = GRID * GRID;
const SPAWN_INTERVAL = 1.1; // seconds between mole spawns
const MOLE_LIFETIME = 1.4; // seconds a mole stays up
const BOMB_PROBABILITY = 0.17; // ~1 in 6
const DWELL_TIME = 0.22; // seconds aim must remain inside hole

const BOMB_SPRITE: HTMLImageElement = (() => {
  const img = new Image();
  img.src = '/assets/fruits/bomb.png';
  return img;
})();
const WIN_THRESHOLD = 6;

interface Hole {
  cx: number;
  cy: number;
  radius: number;
}

interface Mole {
  holeIndex: number;
  isBomb: boolean;
  spawnTime: number; // game time (elapsed)
  expireTime: number;
  /** Hole index → dwell time accumulated by current aim. Resets when aim leaves. */
  dwell: number;
  /** Cosmetic rise progress 0..1, used by render. */
  age: number;
}

interface RenderState {
  holes: Hole[];
  moles: Mole[];
  aim: Point | null;
  aimHoleIndex: number; // -1 if not aiming at a hole
  dwellRatio: number; // 0..1, current aim's progress (for crosshair color)
  hits: number;
  bombsHit: number;
  misses: number;
  bombFailed: boolean;
  elapsed: number;
}

export const pointMole: Microgame = (() => {
  let gctx: GameContext | null = null;
  let elapsed = 0;
  let timeToNextSpawn = 0;
  let hits = 0;
  let bombsHit = 0;
  let misses = 0; // moles that expired without being hit (excludes bombs)
  let bombFailed = false;
  let moles: Mole[] = [];
  let holes: Hole[] = [];
  const state: RenderState = {
    holes: [],
    moles: [],
    aim: null,
    aimHoleIndex: -1,
    dwellRatio: 0,
    hits: 0,
    bombsHit: 0,
    misses: 0,
    bombFailed: false,
    elapsed: 0,
  };

  function rebuildHoles(canvasW: number, canvasH: number): void {
    // Holes fill most of the canvas with comfortable spacing.
    // Use vertical padding ~12% top (HUD) / 8% bottom, horizontal ~8%.
    const padX = canvasW * 0.08;
    const padTop = canvasH * 0.16;
    const padBottom = canvasH * 0.08;
    const gridW = canvasW - 2 * padX;
    const gridH = canvasH - padTop - padBottom;
    const cellW = gridW / GRID;
    const cellH = gridH / GRID;
    const radius = Math.min(cellW, cellH) * 0.38;
    holes = [];
    for (let row = 0; row < GRID; row++) {
      for (let col = 0; col < GRID; col++) {
        const cx = padX + cellW * (col + 0.5);
        const cy = padTop + cellH * (row + 0.5);
        holes.push({ cx, cy, radius });
      }
    }
    state.holes = holes;
  }

  function pickFreeHole(rng: () => number): number {
    const taken = new Set<number>();
    for (const m of moles) taken.add(m.holeIndex);
    const free: number[] = [];
    for (let i = 0; i < NUM_HOLES; i++) {
      if (!taken.has(i)) free.push(i);
    }
    if (free.length === 0) return -1;
    const idx = Math.floor(rng() * free.length);
    const chosen = free[Math.min(idx, free.length - 1)];
    return chosen ?? -1;
  }

  function spawnMole(rng: () => number): void {
    const holeIndex = pickFreeHole(rng);
    if (holeIndex < 0) return;
    const isBomb = rng() < BOMB_PROBABILITY;
    moles = [
      ...moles,
      {
        holeIndex,
        isBomb,
        spawnTime: elapsed,
        expireTime: elapsed + MOLE_LIFETIME,
        dwell: 0,
        age: 0,
      },
    ];
  }

  function aimSource(frame: InputFrame): Point | null {
    return frame.hands.right.pointTarget ?? frame.hands.left.pointTarget ?? null;
  }

  function holeAtPoint(p: Point): number {
    for (let i = 0; i < holes.length; i++) {
      const h = holes[i];
      if (h === undefined) continue;
      const dx = p.x - h.cx;
      const dy = p.y - h.cy;
      if (dx * dx + dy * dy <= h.radius * h.radius) return i;
    }
    return -1;
  }

  return {
    name: '06-point-mole',
    durationSec: DURATION_SEC,
    requires: { hands: true },

    init(c: GameContext): void {
      gctx = c;
      elapsed = 0;
      timeToNextSpawn = 0;
      hits = 0;
      bombsHit = 0;
      misses = 0;
      bombFailed = false;
      moles = [];
      rebuildHoles(c.canvas.width, c.canvas.height);
      state.holes = holes;
      state.moles = moles;
      state.aim = null;
      state.aimHoleIndex = -1;
      state.dwellRatio = 0;
      state.hits = 0;
      state.bombsHit = 0;
      state.misses = 0;
      state.bombFailed = false;
      state.elapsed = 0;
    },

    tick(dt: number, frame: InputFrame): void {
      if (gctx === null || bombFailed) {
        // After bomb fail we still let elapsed advance so end() is reached;
        // but skip all gameplay updates.
        elapsed += dt;
        state.elapsed = elapsed;
        return;
      }

      // Resize-safe: if canvas dims changed, rebuild holes.
      const cw = gctx.canvas.width;
      const ch = gctx.canvas.height;
      if (
        holes.length !== NUM_HOLES ||
        (holes[0] !== undefined &&
          (Math.abs(holes[0].cx - cw * 0.08 - (cw - 2 * cw * 0.08) / (GRID * 2)) > 1 ||
            holes[0].cy < 0))
      ) {
        rebuildHoles(cw, ch);
        state.holes = holes;
      }

      elapsed += dt;
      state.elapsed = elapsed;

      // Spawn moles on cadence.
      timeToNextSpawn -= dt;
      while (timeToNextSpawn <= 0 && elapsed < DURATION_SEC) {
        spawnMole(gctx.rng);
        timeToNextSpawn += SPAWN_INTERVAL;
      }

      // Aim & dwell tracking.
      const aim = aimSource(frame);
      state.aim = aim;
      const aimedHole = aim !== null ? holeAtPoint(aim) : -1;
      state.aimHoleIndex = aimedHole;

      // Update each mole's age and dwell.
      const next: Mole[] = [];
      let bombHitThisFrame = false;
      let dwellRatio = 0;
      for (const m of moles) {
        const updated: Mole = { ...m, age: Math.min(1, (elapsed - m.spawnTime) / 0.18) };
        if (aimedHole === m.holeIndex) {
          updated.dwell = m.dwell + dt;
          if (updated.dwell >= DWELL_TIME) {
            // Hit!
            if (m.isBomb) {
              bombsHit += 1;
              bombHitThisFrame = true;
              gctx.audio.thud();
            } else {
              hits += 1;
              gctx.audio.pop();
            }
            continue; // remove the mole
          }
          dwellRatio = Math.max(dwellRatio, updated.dwell / DWELL_TIME);
        } else {
          updated.dwell = 0;
        }
        // Expire after lifetime.
        if (elapsed >= m.expireTime) {
          if (!m.isBomb) misses += 1;
          continue;
        }
        next.push(updated);
      }
      moles = next;
      state.moles = moles;
      state.dwellRatio = dwellRatio;
      state.hits = hits;
      state.bombsHit = bombsHit;
      state.misses = misses;

      if (bombHitThisFrame) {
        bombFailed = true;
        state.bombFailed = true;
      }
    },

    render(g: CanvasRenderingContext2D): void {
      if (gctx === null) return;
      const { canvas } = gctx;
      const W = canvas.width;
      const H = canvas.height;

      // Background.
      g.fillStyle = '#0e1116';
      g.fillRect(0, 0, W, H);

      // Holes.
      for (const h of state.holes) {
        // Outer rim.
        g.beginPath();
        g.arc(h.cx, h.cy, h.radius, 0, Math.PI * 2);
        g.fillStyle = '#3a2614';
        g.fill();
        // Inner shadow ellipse to suggest a hole.
        g.beginPath();
        g.ellipse(h.cx, h.cy + h.radius * 0.1, h.radius * 0.88, h.radius * 0.6, 0, 0, Math.PI * 2);
        g.fillStyle = '#1c1108';
        g.fill();
      }

      // Moles (drawn on top of holes).
      for (const m of state.moles) {
        const hole = state.holes[m.holeIndex];
        if (hole === undefined) continue;
        const rise = m.age; // 0..1
        const baseY = hole.cy + hole.radius * 0.1;
        const topY = hole.cy - hole.radius * 0.5;
        const bodyCy = baseY + (topY - baseY) * rise;
        const bodyRx = hole.radius * 0.7;
        const bodyRy = hole.radius * 0.7;

        // Clip to hole so the mole appears to come out of it.
        g.save();
        g.beginPath();
        g.arc(hole.cx, hole.cy, hole.radius * 0.96, 0, Math.PI * 2);
        g.clip();

        if (m.isBomb) {
          // Clay bomb sprite (chomp-ios). Slightly bigger than the mole body.
          if (BOMB_SPRITE.complete && BOMB_SPRITE.naturalWidth > 0) {
            const d = Math.max(bodyRx, bodyRy) * 2.4;
            g.drawImage(BOMB_SPRITE, hole.cx - d / 2, bodyCy - d / 2, d, d);
          } else {
            // Fallback while decoding.
            g.beginPath();
            g.ellipse(hole.cx, bodyCy, bodyRx, bodyRy, 0, 0, Math.PI * 2);
            g.fillStyle = '#0a0a0a';
            g.fill();
          }
          // Pulsing red sparkle on top for "danger" affordance.
          const sparkR = hole.radius * 0.16;
          const sx = hole.cx + bodyRx * 0.35;
          const sy = bodyCy - bodyRy * 0.55;
          const sparkAlpha = 0.6 + 0.4 * Math.sin(state.elapsed * 12);
          g.beginPath();
          g.arc(sx, sy, sparkR, 0, Math.PI * 2);
          g.fillStyle = `rgba(255,60,40,${sparkAlpha.toFixed(3)})`;
          g.fill();
        } else {
          // Mole body.
          g.beginPath();
          g.ellipse(hole.cx, bodyCy, bodyRx, bodyRy, 0, 0, Math.PI * 2);
          g.fillStyle = '#8a5a32';
          g.fill();
          // Lighter belly.
          g.beginPath();
          g.ellipse(hole.cx, bodyCy + bodyRy * 0.2, bodyRx * 0.55, bodyRy * 0.4, 0, 0, Math.PI * 2);
          g.fillStyle = '#c89868';
          g.fill();
          // Eyes.
          const eyeR = Math.max(2, hole.radius * 0.08);
          const eyeY = bodyCy - bodyRy * 0.25;
          const eyeDx = bodyRx * 0.32;
          g.fillStyle = '#0c0c0c';
          g.beginPath();
          g.arc(hole.cx - eyeDx, eyeY, eyeR, 0, Math.PI * 2);
          g.fill();
          g.beginPath();
          g.arc(hole.cx + eyeDx, eyeY, eyeR, 0, Math.PI * 2);
          g.fill();
          // Nose.
          g.beginPath();
          g.arc(hole.cx, eyeY + eyeR * 1.7, eyeR * 0.8, 0, Math.PI * 2);
          g.fillStyle = '#3a1a0a';
          g.fill();
        }
        g.restore();
      }

      // Crosshair at current aim.
      if (state.aim !== null) {
        const t = Math.min(1, Math.max(0, state.dwellRatio));
        // Red→green as dwell fills.
        const r = Math.round(255 * (1 - t));
        const grn = Math.round(220 * t + 60 * (1 - t));
        const b = 60;
        const color = `rgb(${r},${grn},${b})`;
        const ax = state.aim.x;
        const ay = state.aim.y;
        const ringR = Math.max(10, Math.min(W, H) * 0.022);
        g.lineWidth = 3;
        g.strokeStyle = color;
        g.beginPath();
        g.arc(ax, ay, ringR, 0, Math.PI * 2);
        g.stroke();
        // Inner dot.
        g.beginPath();
        g.arc(ax, ay, Math.max(2, ringR * 0.18), 0, Math.PI * 2);
        g.fillStyle = color;
        g.fill();
        // Dwell arc.
        if (t > 0 && state.aimHoleIndex >= 0) {
          g.lineWidth = 4;
          g.strokeStyle = color;
          g.beginPath();
          g.arc(ax, ay, ringR + 6, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * t);
          g.stroke();
        }
      }

      // HUD.
      const hudY = Math.round(H * 0.05);
      const fontSize = Math.max(14, Math.round(H * 0.035));
      g.font = `bold ${fontSize}px system-ui, sans-serif`;
      g.textBaseline = 'middle';
      g.fillStyle = '#e8ecf5';
      g.textAlign = 'left';
      g.fillText(`hits: ${state.hits} / ${WIN_THRESHOLD}`, Math.round(W * 0.04), hudY);
      g.textAlign = 'right';
      const remain = Math.max(0, DURATION_SEC - state.elapsed);
      g.fillText(`${remain.toFixed(1)}s`, Math.round(W * 0.96), hudY);

      // Fail overlay.
      if (state.bombFailed) {
        g.fillStyle = 'rgba(120,10,10,0.55)';
        g.fillRect(0, 0, W, H);
        g.fillStyle = '#fff';
        g.textAlign = 'center';
        g.font = `bold ${Math.round(H * 0.07)}px system-ui, sans-serif`;
        g.fillText('BOOM', W / 2, H / 2);
      }
    },

    end(): MicrogameResult {
      const won = !bombFailed && hits >= WIN_THRESHOLD;
      if (gctx !== null) {
        if (won) gctx.audio.win();
        else gctx.audio.lose();
      }
      return {
        microgame: '06-point-mole',
        won,
        score: hits,
        detail: { bombsHit, misses },
      };
    },
  };
})();
